import React, { useEffect, useRef, useState } from "react";
import styles from "./DropdownLocalAlgoliaWithSearch.module.scss";
import Text from "components/text/Text";
import { useRefinementList } from 'react-instantsearch';
import { getAllClassNames } from "helpers";
//import { useClickOutside } from "hooks";
import { Arrow, Close } from "assets/icons";
//import { HighlightText } from "components/highlightText/HighlightText";
import strings from "assets/strings";

export type SearchProps = {
  title?: string;
  width?: number;
  marginTop?: number;
  placeholder?: string;
  attribute: string;
};

export const DropdownLocalAlgoliaWithSearch = ({
  title,
  width,
  marginTop,
  attribute,
}: SearchProps) => {
  const {
    items,
    refine,
    searchForItems,
  } = useRefinementList({ attribute });
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const arrowRef = useRef<SVGSVGElement>(null);
  const [id] = useState(`searchbox-${attribute}`);
  const [bubbles, setBubbles] = useState<Array<string>>([]);
  const [localItems, setLocalItems] = useState(items);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const checkboxRefs = useRef<Array<HTMLInputElement | null>>([]);
  const prevItemsRef = useRef(items);
  //const regex = /^[a-zA-Z0-9\u0590-\u05FF\s]+$/;

  useEffect(() => {
    const itemsChanged = JSON.stringify(prevItemsRef.current) !== JSON.stringify(items);

    if (itemsChanged) {
      prevItemsRef.current = items;
      // Sort and update `localItems`
      const sortedItems = [...items].sort((a, b) => a.label.localeCompare(b.label));
      if (JSON.stringify(localItems) !== JSON.stringify(sortedItems)) {
        setLocalItems(sortedItems);
      }
      // // Update `bubbles` based on refined items only if necessary
      // const refinedValues = items.filter((item) => item.isRefined).map((item) => item.value);
      // if (JSON.stringify(bubbles) !== JSON.stringify(refinedValues)) {
      //   setBubbles(refinedValues);
      // }
    }
  }, [items, localItems, bubbles]);

  const handleSelectItem = (value: string) => {
    const selectedIndex = localItems.findIndex(item => item.value === value);
    const newBubbles = bubbles.includes(value)
      ? bubbles.filter((bubble) => bubble !== value) // Remove value
      : [...bubbles, value]; // Add value
    // Update `bubbles` only if the new state differs
    if (JSON.stringify(newBubbles) !== JSON.stringify(bubbles)) {
      setBubbles(newBubbles);
    }
    // Refine the value in Algolia
    refine(value);
    // Reset input and close dropdown
    setInputValue('');

    setTimeout(() => {
      checkboxRefs.current[selectedIndex]?.focus();
    }, 0);
    //setShowDropdown(false);
  };

  useEffect(() => {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);

    // Use the spread operator with the 'params' iterator and filter 'name' keys
    const namesArray: string[] = [...params].filter(([key]) => key === attribute).map(([_, value]) => value);

    setBubbles(namesArray.map(name => decodeURIComponent(name))); // Set the bubbles state with the filtered names

  }, []);

  const handleFocus = () => {
    //setShowDropdown(true);
    setHighlightedIndex(0); // Set focus to the first element of the checkbox list
  };

  // Handle Input Blur: Keep dropdown open if focus moves to dropdown
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      e.relatedTarget === dropdownRef.current || // Focus moved to dropdown
      dropdownRef.current?.contains(e.relatedTarget as Node)
    ) {
      return;
    }
    setShowDropdown(false);
  };

  // Close dropdown when clicking outside the input or dropdown
  const handleClickOutside = (e: MouseEvent) => {
    // Check if the click target is the arrow or its children
    const isArrowClicked = arrowRef.current && 
      (arrowRef.current === e.target || arrowRef.current.contains(e.target as Node));
    
    // If arrow was clicked, don't do anything here (let the arrow click handler manage toggle)
    if (isArrowClicked) {
      return;
    }
    
    // Otherwise, check if click was outside dropdown and input
    if (
      inputRef.current &&
      !inputRef.current.contains(e.target as Node) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setShowDropdown(false);
      setInputValue('');
    }
  };

  // Attach the click outside listener
  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // useEffect(() => {
  //   if (!showDropdown) {      
  //     inputRef.current?.focus();
  //   }
  // }, [showDropdown]);


  useEffect(() => {
    const handleEscapePress = (e: KeyboardEvent) => {
      if (e.key === "Escape") {       
        setShowDropdown(false);
      }
    };

    document.addEventListener("keydown", handleEscapePress);
    return () => document.removeEventListener("keydown", handleEscapePress);
  }, []);


  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.currentTarget.value;
    const hasValidCharacters = /[a-zA-Z0-9\u0590-\u05FF]/.test(inputValue);

    setInputValue(inputValue);
    setShowDropdown(true);
    if (inputValue === '') {
      // If input is cleared, reset localItems to original items
      setLocalItems(items);
      setShowDropdown(false);
      searchForItems('');
    } else if (hasValidCharacters) {
      searchForItems(inputValue); // Trigger Algolia's search for valid characters
    } else {
      setLocalItems([]); // Show no items for invalid input
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
   
    if (showDropdown) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prevIndex) => {
            const newIndex = prevIndex === null || prevIndex === localItems.length - 1 ? 0 : prevIndex + 1;
            checkboxRefs.current[newIndex]?.focus();
            return newIndex;
          });
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prevIndex) => {
            const newIndex = prevIndex === null || prevIndex === 0 ? localItems.length - 1 : prevIndex - 1;
            checkboxRefs.current[newIndex]?.focus();
            return newIndex;
          });
          break;
        case "Enter":
        case " ":  // Adding support for the space bar
          if (document.activeElement === inputRef.current) {
            return;
          }
          e.preventDefault();
          if (highlightedIndex !== null) {
            handleSelectItem(localItems[highlightedIndex].value);
          }
          break;
        case "Escape":
          setShowDropdown(false);
          // setTimeout(() => {
          //   inputRef.current?.focus();
          // }, 0);
          break;
        case "Tab":
          // If Shift+Tab is pressed, move focus back to the input field
          if (e.shiftKey) {
            e.preventDefault();
            inputRef.current?.focus();
            setHighlightedIndex(null);
          } 
          // Regular Tab behavior (only if there are items)
          else if (localItems.length > 0) {
            e.preventDefault();
            checkboxRefs.current[0]?.focus();
            setHighlightedIndex(0);
          }
          break;
        default:
          break;
      }
    }
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent<SVGSVGElement>, item: { value: string; label: string; hasSubField?: boolean }) => {
    if (event.key === "Enter") {
      handleSelectItem(item.value);
    }
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent the click from being detected by other listeners
    e.stopPropagation();
    
    // Toggle the dropdown state
    setShowDropdown(prev => !prev);
    
    // If we're closing the dropdown, also reset the input
    if (showDropdown) {
      setInputValue('');
    }
  };

  return (
    <div
      className={getAllClassNames(["ais-SearchBox", styles.container])}
      style={{ marginTop }}
    >
      {title && <Text className={styles.title} text={title} labelFor={id} />}
      <form
        action=""
        className={getAllClassNames([
          styles.form,
          showDropdown && styles.formActive,
        ])}
        noValidate
        onSubmit={(e) => e.preventDefault()} // Prevent form submission
      >
        {bubbles.length > 0 && (
          <ul> {
            bubbles.map((bubble, index) => (
              <li
                key={index}
                className={getAllClassNames([
                  styles.bubble,
                  "ais-RefinementList-item",
                ])}
              >
                <span
                  className={getAllClassNames([
                    "ais-RefinementList-labelText",
                    styles.label,
                  ])}
                >
                  {bubble}
                </span>
                <Close
                  onClick={() => handleSelectItem(bubble)}
                  className={styles.icon}
                  aria-label={`${strings.globalStrings.remove} ${bubble}`} // Added aria-label for accessibility
                  role="button" // Added role for screen readers
                  tabIndex={0} // Make the element focusable
                  onKeyDown={(event) => handleCloseKeyDown(event, { label: bubble, value: bubble })} // Add key down event listener
                />
              </li>
            ))}
          </ul>)
        }
        <input
          aria-autocomplete="list" aria-activedescendant="autocomplete-list" aria-owns="autocomplete-list" role="combobox"
          ref={inputRef}
          className="ais-SearchBox-input"
          type="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={512}
          id={id}
          value={inputValue}
          onKeyDown={handleKeyDown} // Add key down event listener
          onChange={(event) => { handleInputChange(event); }}

        />
        <Arrow
          ref={arrowRef}
          className={styles.iconArrow}
          onClick={handleArrowClick}
          aria-label={`${showDropdown ? strings.globalStrings.collapse : strings.globalStrings.expand} ${title}`} // Added aria-label for accessibility
          role="button" // Added role for screen readers
          tabIndex={0} // Make the element focusable
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { // Added support for the space bar
              e.preventDefault();
              setShowDropdown(prev => !prev);
            }
          }}
        //onKeyDown={handleArrowKeyDown}
        />
      </form>
      {showDropdown && (
        <div role="listbox"
          id="autocomplete-list"
          aria-expanded={showDropdown ? 'true' : 'false'}
          ref={dropdownRef}
          className={styles.dropdown}
          style={{ right: width, borderEndEndRadius: localItems.length > 4 ? '8px' : '0px'  }}
        >
          {localItems.length > 0 ? (
            localItems.filter(item => item.label) // Ensure item.label is defined
              .map((item, index) => (
                <div
                  key={item.value}
                  className={`dropdown-item-${index} ${getAllClassNames([
                    "ais-RefinementList-item",
                    item.isRefined && "ais-RefinementList-item--selected",
                  ])}`}
                >
                  <label>
                    <input
                      type="checkbox"
                      ref={(el) => (checkboxRefs.current[index] = el)}
                      value={item.value}
                      checked={(item.isRefined)}
                      className={styles.checkbox}
                      onChange={() => { handleSelectItem(item.value); }}
                      onKeyDown={(e) => handleKeyDown(e)}
                      tabIndex={0}
                    />
                    {item.label}
                  </label>
                </div>
              ))
          ) : (
            inputValue !== '' && <div className={styles.noResults}>{strings.globalStrings.noResults}</div>
          )}
        </div>
      )}
    </div> 
  );
};
